import React, { useEffect, useMemo, useState } from "react";
import { Box, MenuItem, Select } from "@mui/material";
import {
  tokens,
  fmtNumber,
  SignalBar,
  StatusPill,
  ClinicalDataGrid,
  ClinicalPageShell,
} from "../../shared/clinical-design";
import OnboardingDetailPanel from "../components/OnboardingDetailPanel";
import { getOnboardingPipeline } from "../services/onboardingService";
import {
  LOCATIONS,
  DEFAULT_LOCATION,
  CHECKLIST_STEPS,
  STAGE_META,
} from "../common/constants";

/** Mini barra de progreso del checklist (4 segmentos, sin gráficos pesados). */
function ChecklistProgress({ checklist }) {
  return (
    <Box sx={{ display: "inline-flex", gap: "3px", verticalAlign: "middle" }}>
      {CHECKLIST_STEPS.map((s) => (
        <Box
          key={s.key}
          title={s.label}
          sx={{
            width: 18,
            height: 8,
            borderRadius: "2px",
            bgcolor: checklist[s.key] ? tokens.ok : tokens.mutedBg,
            border: `1px solid ${checklist[s.key] ? tokens.ok : tokens.line}`,
          }}
        />
      ))}
    </Box>
  );
}

const COLUMNS = [
  { key: "name", header: "Member" },
  {
    key: "enrollmentDate",
    header: "Enrolled",
    mono: true,
    width: 100,
  },
  {
    key: "daysSinceEnrollment",
    header: "Day",
    align: "right",
    mono: true,
    width: 55,
    render: (r) => (
      <Box
        component="span"
        sx={{
          color:
            r.daysSinceEnrollment > 60
              ? tokens.crit
              : r.daysSinceEnrollment > 30
              ? tokens.warn
              : tokens.ink,
        }}
      >
        {r.daysSinceEnrollment}
      </Box>
    ),
  },
  {
    key: "progress",
    header: "Checklist",
    width: 110,
    render: (r) => <ChecklistProgress checklist={r.checklist} />,
  },
  {
    key: "stage",
    header: "Stage",
    render: (r) => {
      const meta = STAGE_META[r.stage] || STAGE_META.NOT_STARTED;
      return <StatusPill tone={meta.tone} label={meta.label} dense />;
    },
  },
];

/**
 * Module I — Onboarding Journey (New Member Onboarding).
 * Pipeline de nuevos miembros con progreso del checklist (welcome call,
 * records request, first appointment, HRA) y detalle con journey tracker.
 */
export default function MemberOnboardingPage() {
  const [locationKey, setLocationKey] = useState(DEFAULT_LOCATION);
  const [data, setData] = useState({ members: [] });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getOnboardingPipeline(locationKey)
      .then((d) => {
        if (!active) return;
        setData(d);
        setSelectedId(d.members[0]?.cipId ?? null);
      })
      .catch(() => {
        if (!active) return;
        setData({ members: [] });
        setSelectedId(null);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [locationKey]);

  const selectedRow = useMemo(
    () => data.members.find((m) => m.cipId === selectedId) || null,
    [data, selectedId]
  );

  const signals = useMemo(() => {
    const rows = data.members;
    const total = rows.length;
    const pct = (n) => (total ? `${Math.round((n / total) * 100)}%` : "0%");
    const contacted = rows.filter((r) => r.checklist.welcomeCall).length;
    const withAppt = rows.filter((r) => r.checklist.firstAppointment).length;
    const hraDone = rows.filter((r) => r.checklist.hraCompleted).length;
    const stalled = rows.filter(
      (r) => r.daysSinceEnrollment > 60 && !r.checklist.firstAppointment
    ).length;
    return [
      { label: "New members", value: fmtNumber(total), tone: "info" },
      {
        label: "Contacted",
        value: pct(contacted),
        tone: contacted / (total || 1) >= 0.75 ? "ok" : "warn",
        hint: "Welcome call completed",
      },
      {
        label: "First appt done",
        value: pct(withAppt),
        tone: withAppt / (total || 1) >= 0.5 ? "ok" : "warn",
      },
      { label: "HRA completed", value: pct(hraDone), tone: hraDone / (total || 1) >= 0.3 ? "ok" : "warn" },
      {
        label: "Stalled (60d+)",
        value: stalled,
        tone: stalled > 0 ? "crit" : "ok",
        hint: "Past day 60 of the 90-day window without a first appointment",
      },
    ];
  }, [data]);

  const locationLabel =
    LOCATIONS.find((l) => l.key === locationKey)?.label || locationKey;

  return (
    <ClinicalPageShell
      title="Onboarding Journey"
      subtitle="New member pipeline: welcome call, records request, first appointment and HRA inside the 90-day window — the first risk-capture opportunity of the year."
      moduleTag="Module I"
      devPreview
      actions={
        <Select
          size="small"
          value={locationKey}
          onChange={(e) => setLocationKey(e.target.value)}
          sx={{ fontSize: 13, minWidth: 170, bgcolor: tokens.surface }}
        >
          {LOCATIONS.map((l) => (
            <MenuItem key={l.key} value={l.key} sx={{ fontSize: 13 }}>
              {l.label}
            </MenuItem>
          ))}
        </Select>
      }
    >
      <SignalBar signals={signals} sx={{ mb: 1.5 }} />
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "flex-start",
          flexDirection: { xs: "column", lg: "row" },
        }}
      >
        <Box sx={{ width: { xs: "100%", lg: "45%" }, flexShrink: 0 }}>
          <ClinicalDataGrid
            columns={COLUMNS}
            rows={data.members}
            rowKey={(r) => r.cipId}
            loading={loading}
            onRowClick={(r) => setSelectedId(r.cipId)}
            isRowSelected={(r) => r.cipId === selectedId}
            emptyMessage="No new members in onboarding for this location."
            maxHeight={620}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <OnboardingDetailPanel
            key={selectedRow?.cipId ?? "none"}
            row={selectedRow}
            locationLabel={locationLabel}
          />
        </Box>
      </Box>
    </ClinicalPageShell>
  );
}
